import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Box, CircularProgress, Alert, Button, Container } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTaxBinder } from '@/hooks/api/useTaxBinders';
import { useTaxBinderTaxReturns } from '@/hooks/api/useTaxBinderTaxReturns';
import { GuidedSigningWizard } from '@/features/tax-binders/signing-wizard';
import { taxReturnQueryOptions, taxBinderQueryOptions } from '@/lib/query/options';
import { QueryErrorBoundary } from '@/components/query/QueryErrorBoundary';

export const Route = createFileRoute('/tax-binders/$binderId/sign-returns')({
  loader: async ({ context: { queryClient }, params: { binderId } }) => {
    // Get auth state from query cache (client auth)
    const authState = queryClient.getQueryData(['auth', 'session']) as { token?: string } | undefined;
    const token = authState?.token;

    if (token && binderId) {
      // Prefetch binder and tax returns in parallel
      await Promise.all([
        queryClient.ensureQueryData(taxBinderQueryOptions.detail(binderId, token)),
        queryClient.ensureQueryData(taxReturnQueryOptions.list(binderId, token)),
      ]);
    }
  },
  errorComponent: QueryErrorBoundary,
  component: SignReturnsPage,
});

/**
 * Sign Returns Page
 *
 * Guided wizard for signing all pending tax return authorizations (Form 8879).
 * Supports multi-signer scenarios with sequential signing.
 */
function SignReturnsPage() {
  const { binderId } = Route.useParams();
  const navigate = useNavigate();

  const { data: binder, isLoading: isLoadingBinder, error: binderError } = useTaxBinder(binderId);
  const { data: taxReturns, isLoading: isLoadingReturns, error: returnsError } = useTaxBinderTaxReturns(binderId);

  const handleBack = () => {
    navigate({
      to: '/tax-binders/$binderId/returns',
      params: { binderId },
    });
  };

  // Loading state
  if (isLoadingBinder || isLoadingReturns) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (binderError || returnsError || !binder) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load tax return information. Please try again.
        </Alert>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={handleBack}>
          Return to Tax Returns
        </Button>
      </Container>
    );
  }

  // No returns to sign
  if (!taxReturns || taxReturns.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          There are no tax returns requiring your signature at this time.
        </Alert>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={handleBack}>
          Return to Tax Returns
        </Button>
      </Container>
    );
  }

  // Render the wizard
  return <GuidedSigningWizard binderId={binderId} binder={binder} taxReturns={taxReturns} />;
}
